// client/src/components/marketer/modals/MarketerResultStatusModal.js
import React, { useState, useEffect, useCallback } from 'react';
import ResultViewModal from '../result/modals/ResultViewModal';
import '../../../styles/marketer/modals/MarketerResultStatusModal.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function MarketerResultStatusModal({ open, onClose, projectId, projectNumber }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModal, setViewModal] = useState({
    open: false,
    result: null
  });

  // 결과물 목록 조회
  const fetchResults = useCallback(async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/marketer/projects/${projectId}/results`, {
        credentials: 'include'
      });
      const data = await res.json();
      
      if (data.ok) {
        setResults(data.results || []);
      } else {
        alert('결과물 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('결과물 목록 조회 오류:', error);
      alert('결과물 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchResults();
    }
  }, [open, projectId, fetchResults]);

  // 결과물 보기
  const handleViewResult = (result) => {
    setViewModal({
      open: true,
      result: result
    });
  };

  // SNS 아이콘 (SVG 사용)
  const getSNSIcon = (platform) => {
    const iconPath = `/images/sns/${platform}.svg`;
    return (
      <img 
        src={iconPath} 
        alt={platform} 
        style={{ width: 16, height: 16 }}
        onError={(e) => { e.target.style.display = 'none'; }}
      />
    );
  };

  // 팔로워 수 포맷
  const formatFollowers = (count) => {
    if (!count) return '-';
    if (count >= 10000) {
      return `${Math.floor(count / 10000)}만`;
    }
    return count.toLocaleString();
  };

  // 상태 텍스트 반환
  const getStatusText = (status) => {
    const statusMap = {
      'none': '제출 대기',
      'submitted': '승인 대기',
      'approved': '승인 완료',
      'rejected': '거절 (관리자 검토 중)',
      'admin_reviewing': '셀프애드 팀 검토 중',
      'admin_approved': '셀프애드 팀 승인',
      'admin_rejected': '셀프애드 팀 거절',
      'expired': '기한 내 미제출',
      'completed': '완료'
    };
    return statusMap[status] || status;
  };

  // 상태 클래스 반환
  const getStatusClass = (status) => {
    const classMap = {
      'none': 'marketer-result-status-modal__status--none',
      'submitted': 'marketer-result-status-modal__status--submitted',
      'approved': 'marketer-result-status-modal__status--approved',
      'rejected': 'marketer-result-status-modal__status--rejected',
      'admin_reviewing': 'marketer-result-status-modal__status--admin-reviewing',
      'admin_approved': 'marketer-result-status-modal__status--admin-approved',
      'admin_rejected': 'marketer-result-status-modal__status--admin-rejected',
      'expired': 'marketer-result-status-modal__status--expired',
      'completed': 'marketer-result-status-modal__status--completed'
    };
    return classMap[status] || '';
  };

  if (!open) return null;

  return (
    <>
      {/* 오버레이 */}
      <div className="marketer-result-status-modal mbm-overlay">
        {/* 모달 */}
        <div className="marketer-result-status-modal__content mbm" onClick={(e) => e.stopPropagation()}>
          {/* 헤더 */}
          <div className="marketer-result-status-modal__header mbm-head">
            <div>
              <h2 className="marketer-result-status-modal__title mbm-title">
                결과물 상태 보기
              </h2>
              <div className="marketer-result-status-modal__subtitle">
                프로젝트: {projectNumber || 'N/A'}
              </div>
            </div>
            <button onClick={onClose} className="marketer-result-status-modal__close mbm-close">
              ✕
            </button>
          </div>

          {/* 본문 */}
          <div className="marketer-result-status-modal__body">
            {loading ? (
              <div className="marketer-result-status-modal__loading">
                로딩 중...
              </div>
            ) : results.length === 0 ? (
              <div className="marketer-result-status-modal__empty">
                계약된 인플루언서가 없습니다.
              </div>
            ) : (
              <table className="marketer-result-status-modal__table">
                <thead>
                  <tr>
                    <th>NO</th>
                    <th>계약한 인플루언서</th>
                    <th>결과물 보기</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => {
                    const canViewResult = result.resultStatus === 'submitted' || 
                                        result.resultStatus === 'approved' || 
                                        result.resultStatus === 'rejected' ||
                                        result.resultStatus === 'admin_reviewing' ||
                                        result.resultStatus === 'admin_approved';

                    return (
                      <tr key={result.id}>
                        <td>{index + 1}</td>
                        <td>
                          <div className="marketer-result-status-modal__influencer-name">
                            {result.influencerName || 'N/A'}
                          </div>
                          {/* SNS 계정 표시 */}
                          {result.snsAccounts && result.snsAccounts.length > 0 ? (
                            <div style={{ 
                              display: 'flex', 
                              gap: 6, 
                              flexWrap: 'wrap', 
                              marginTop: 8,
                              alignItems: 'flex-start'
                            }}>
                              {result.snsAccounts.map((sns, idx) => (
                                <a
                                  key={idx}
                                  href={sns.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    gap: 5, 
                                    textDecoration: 'none', 
                                    color: '#3b4658', 
                                    fontSize: 12,
                                    padding: '5px 10px',
                                    background: '#f3f4f6',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#e5e7eb';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#f3f4f6';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  {getSNSIcon(sns.platform)}
                                  <span style={{ fontWeight: 600, color: '#0b1324' }}>
                                    {sns.accountName}
                                  </span>
                                  <span style={{ color: '#9ca3af', fontSize: 11 }}>
                                    ({formatFollowers(sns.followerCount)})
                                  </span>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                              SNS 없음
                            </div>
                          )}
                        </td>
                        <td>
                          {canViewResult ? (
                            <button
                              onClick={() => handleViewResult(result)}
                              className="marketer-result-status-modal__view-button"
                            >
                              보기
                            </button>
                          ) : (
                            <span className="marketer-result-status-modal__view-disabled">-</span>
                          )}
                        </td>
                        <td>
                          <span className={`marketer-result-status-modal__status ${getStatusClass(result.resultStatus)}`}>
                            {getStatusText(result.resultStatus)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* 푸터 */}
          <div className="marketer-result-status-modal__footer mbm-foot">
            <button onClick={onClose} className="mb-btn mb-btn-secondary">
              닫기
            </button>
          </div>
        </div>
      </div>

      {/* 결과물 보기 모달 */}
      {viewModal.open && (
        <ResultViewModal
          open={viewModal.open}
          onClose={() => setViewModal({ open: false, result: null })}
          result={viewModal.result}
        />
      )}
    </>
  );
}
